/**
 * Section Component
 *
 * Displays a section containing multiple card components.
 * Supports feature flags for both the section and individual cards.
 */

'use client';

import { Card } from '@/@cms/types/entities';
import { SectionComponentProps } from '@/@cms/types/props';
import React from 'react';
import CardComponent from './Card';

/**
 * Section component that displays a group of related cards
 *
 * Features:
 * - Labeled section with appropriate spacing
 * - Grid layout for cards with responsive design
 * - Passes feature flags to child components
 * - Template variable resolution
 *
 * @param props - Section component props
 * @returns Section component
 */
const SectionComponent: React.FC<SectionComponentProps> = ({
    data,
    onCardClick,
    resolveTemplate,
    className = '',
    isLoading = false,
}) => {
    // Process section name with template if provided
    const sectionName = resolveTemplate && data.Name ? resolveTemplate(data.Name) : data.Name;

    /**
     * Handle card click event
     * @param card - The card that was clicked
     */
    const handleCardClick = (card: Card) => {
        if (onCardClick) {
            onCardClick(card);
        }
    };

    // Show loading state if needed
    if (isLoading) {
        return (
            <section className={`section section-loading mb-10 ${className}`}>
                <div className="section-header mb-4 animate-pulse">
                    <div className="h-6 w-1/4 rounded bg-gray-200"></div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-64 animate-pulse rounded bg-gray-100"></div>
                    ))}
                </div>
            </section>
        );
    }

    // No cards to display
    if (!data.Cards || data.Cards.length === 0) {
        return null;
    }

    return (
        <section className={`section mb-10 ${className}`}>
            {/* Section header with name */}
            {sectionName && <h2 className="mb-4 text-medium font-semibold">{sectionName}</h2>}

            {/* Card grid with responsive columns */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {data.Cards.map((card) => (
                    <CardComponent
                        key={card.id}
                        data={card}
                        sectionName={sectionName}
                        onClick={handleCardClick}
                        resolveTemplate={resolveTemplate}
                        isDisabled={card.isDisabled}
                    />
                ))}
            </div>
        </section>
    );
};

export default SectionComponent;
